// Single owner of the BLOCK-EDITOR validation rules shared by the 참고 자료 features.
//
// PRD 10 v2.5/v2.6 ("3개 콘텐츠 화면 블록 에디터 통일") and PRD 11 v1.4 ("블록 검증 규칙 …
// 3곳 통일·3곳 프로토 동일 구현") specify ONE rule set for 건강백과 · 상담매뉴얼 · 관리지침.
// Before this module the rules lived in a hand-copied file per feature and had already
// drifted apart in five places (whitespace-only video title accepted in one, no image
// format/size pre-check in another, no caps on the image title / description in a third).
// The rules now live here once; each feature keeps only what its own PRD defines
// differently (see "Deliberately NOT here" below).
//
// The backend `BlockContentValidator` is authoritative — every function below mirrors it
// so the inline error and the server's rejection can never disagree.
//
// Rules (identical in PRD 10 §3.7 and PRD 11 §3.4):
//   • 최대 10 blocks per content.
//   • TEXT block: 순수텍스트 1~500자; 빈/공백만 = 무효.
//   • 하이퍼링크: `https://` only.
//   • IMAGE block: JPG/PNG only, ≤5MB, 1블록 1이미지, 교체 불가.
//   • VIDEO block: URL only — `https://` + YouTube/Vimeo (ADM.014); 제목 선택·1~50자·공백무효 (ADM.018).
//   • 이모지 전 클래스 불허 ([공통] 00 §4.2).
//
// Deliberately NOT here — each feature's own PRD decides these:
//   • the minimum block count (건강백과 §4.3 = 필수 1개 이상; 상담매뉴얼 §4.2 = 0개도 저장 가능);
//   • 제목 length (건강백과 100 / 상담매뉴얼 200 — [공통] 00 §8 개별 유지 확정);
//   • the 임시저장 relaxation (상담매뉴얼 §3.4 only);
//   • which i18n key renders each violation (the two features ship different copy).

import { decodeHtmlEntities } from './decode-html-entities';
import { hasEmoji, hasEmojiInRichText } from './emoji';
import { isAllowedVideoUrl } from './video-embed';

// PRD 10 §3.7 / PRD 11 §3.4, confirmed by clarification ADM.015/ADM.016
// ("콘텐츠당 최대 블록 수: 10개 / 텍스트 블록 최대 길이: 500자", 2026-06-29).
export const MAX_BLOCKS = 10;
export const TEXT_BLOCK_MAX = 500;

// SHCT-370 — "can another block be added?" asked in ONE place. PRD 07 §3.2 line 126 (and the matching
// clauses in PRD 10 / PRD 11) require the ADD itself to be blocked at the cap, not just the save, so the
// rule is needed twice per screen: once to disable the buttons, once to guard the state updater against a
// double-click or keyboard repeat slipping past a disabled control. Two hand-written `>= MAX_BLOCKS`
// comparisons would be two places to get the boundary wrong.
export function canAddBlock(currentCount: number): boolean {
  return currentCount < MAX_BLOCKS;
}

// ADM.018 — 영상 제목: 선택·1~50자·공백만 무효. [공통] 00 §8 ratifies the same bound for
// BOTH features ("영상 제목 (10·11) | 1~50 | NAME 상당 — 일치").
export const VIDEO_TITLE_MAX = 50;

// 이미지 제목 / 이미지·영상 설명 are absent from both feature PRDs AND from the §8
// 전수조사 appendix, so [공통] 00 §3 assigns them by its default table
// ("신규 필드와 기존 미정의 필드는 이 표를 기본값으로 적용"):
//   이미지 제목      → 명칭(NAME) 1~50
//   이미지·영상 설명 → 설명(DESC) 1~200 (멀티라인 → DESC per §3)
// Both fields are optional (PRD "제목(선택)" / "텍스트 설명 노출" toggle), so an empty value
// is valid and only the upper bound is enforced.
// 명칭(NAME) 1~50 — the bound for any 블록 제목. 상담매뉴얼's TEXT block also carries a 제목
// (PRD 11 §3.6); it is undefined in every PRD too, so it takes the same §3 default.
export const BLOCK_TITLE_MAX = 50;
export const IMAGE_TITLE_MAX = BLOCK_TITLE_MAX;
export const BLOCK_DESC_MAX = 200;

// ADM.013 / ADM.017 — 이미지 = JPG/PNG only, 최대 5MB. 해상도 제한 없음(원본 저장).
export const IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const IMAGE_MIME_TYPES: readonly string[] = ['image/jpeg', 'image/png'];
export const IMAGE_ACCEPT = 'image/jpeg,image/png';

/**
 * The block fields the shared rules read. Both features' `ContentBlock` are structurally
 * assignable to this (they differ only in which fields they mark optional), so neither has
 * to change its own wire type to use this module.
 */
export interface ValidatableBlock {
  type: 'text' | 'image' | 'video';
  /** text block → WYSIWYG body; image/video block → the 설명 shown under the media. */
  text?: string;
  /** image/video block → 제목 (optional). */
  title?: string;
  /** image block → uploaded URL. */
  dataUrl?: string;
  /** video block → external URL. */
  url?: string;
  /** image/video block → "텍스트 설명 노출" toggle. */
  showText?: boolean;
}

/**
 * Plain-text length of a WYSIWYG block body — the value the 1~500자 rule counts.
 *
 * MUST mirror the backend authority `BlockContentValidator.plainText`, otherwise the FE
 * counter and the BE check disagree and a save is rejected while the counter still reads
 * ≤500. The BE does: strip each tag to a SPACE (so a `<div>`/`<p>`/`<br>` line break counts),
 * decode HTML entities (a user-typed `<`/`&` persisted as `&lt;`/`&amp;` counts as 1 visible
 * char, a `&nbsp;`-only block collapses to empty), then collapse whitespace runs and trim.
 * The stored HTML is still sent to the BE unchanged.
 */
export function plainTextLength(html: string | undefined | null): number {
  const withoutTags = (html || '').replace(/<[^>]*>/g, ' ');
  return decodeHtmlEntities(withoutTags).replace(/\s+/g, ' ').trim().length;
}

/**
 * ADM.016 — a text-block hyperlink must be a `https://` URL (any host).
 *
 * SHCT-370 Change 4 ("Validate URL format before saving. Reject invalid URLs.", and PRD 07 §3
 * line 129 "`https://` 유효 URL만") asks for TWO things, and this used to do only the first: the
 * scheme test alone accepted a bare `https://` and `https://a b`, which are not URLs at all. They
 * were then inserted as an href, saved, and served as a dead link — the server's allow-list checks
 * the SCHEME of an href, never whether the rest parses. Parsing here is the only place either side
 * of the wire judges the URL's shape.
 *
 * `new URL` throws on an unparseable input and yields an empty hostname for a host-less one
 * (`https:///path`), so both forms are rejected. A space inside the PATH is not an error — the
 * parser percent-encodes it, which is what a browser does with the same link.
 */
export function isHttpsUrl(url: string | undefined | null): boolean {
  const trimmed = (url ?? '').trim();
  if (!/^https:\/\//i.test(trimmed)) return false;
  try {
    return new URL(trimmed).hostname.length > 0;
  } catch {
    return false;
  }
}

/**
 * ADM.013 / ADM.017 — JPG/PNG only, ≤5MB. Checked client-side for instant feedback; the BE
 * re-validates authoritatively with a magic-byte sniff. 해상도는 검사하지 않는다 (원본 저장).
 */
export function isImageFileValid(file: File): boolean {
  return IMAGE_MIME_TYPES.includes(file.type) && file.size <= IMAGE_MAX_BYTES;
}

/**
 * ADM.018 — the video 제목 is OPTIONAL: absent or left empty is valid. Only a title the author
 * actually typed is bound-checked, and a whitespace-only string is the one blank form that IS
 * invalid (it renders as an empty caption while looking filled-in). Mirrors
 * `BlockContentValidator.validateVideoTitle`.
 */
export function isVideoTitleValid(title: string | undefined | null): boolean {
  if (title == null || title === '') return true;
  const trimmed = title.trim();
  return trimmed.length > 0 && trimmed.length <= VIDEO_TITLE_MAX;
}

/** 이미지 제목 — optional ([공통] 00 §3 NAME); empty is valid, only the 50-char bound applies. */
export function isImageTitleValid(title: string | undefined | null): boolean {
  return (title ?? '').trim().length <= IMAGE_TITLE_MAX;
}

/** 이미지·영상 설명 — optional ([공통] 00 §3 DESC); empty is valid, only the 200-char bound applies. */
export function isBlockDescValid(text: string | undefined | null): boolean {
  return (text ?? '').trim().length <= BLOCK_DESC_MAX;
}

/**
 * A block "has content": text → non-empty plain text (빈/공백만 무효, ADM.016); image → an
 * uploaded URL; video → a URL. Mirrors the BE's per-type EMPTY_BLOCK check.
 */
export function blockHasContent(b: ValidatableBlock): boolean {
  if (b.type === 'text') return plainTextLength(b.text) > 0;
  if (b.type === 'image') return Boolean(b.dataUrl);
  if (b.type === 'video') return Boolean((b.url ?? '').trim());
  return false;
}

/**
 * SHCT-662 — drop the media blocks the author never filled in.
 *
 * PRD 10 v2.13 §3.7 (mirrored into PRD 07 v1.14 §3 and PRD 11 v1.10 §3, so this is a
 * three-screen rule and belongs here): "이미지가 업로드되지 않은 이미지 블록과 URL이 비어 있는
 * 영상 블록은 저장 시 제외한다(블록 자체를 버린다). 저장을 차단하지 않는다."
 *
 * The bug it fixes: adding an image block and saving before uploading anything used to fail
 * the 본문 필수 조건 with "최소 1개 블록에 내용을 입력해주세요." even when a filled text block sat
 * right above it. §4.3 now judges that condition on VALID blocks only, so callers must
 * validate and submit THIS array — validating the raw one and sending the filtered one (or
 * the reverse) reintroduces the same disagreement in a subtler form.
 *
 * A blank TEXT block is deliberately NOT dropped: §3.7 calls it 무효 and the customer's
 * 2026-08-21 answer left the text-block rules unchanged. Silently discarding structure the
 * author typed is a different thing from discarding a media block they never populated.
 */
export function withoutEmptyMediaBlocks<T extends ValidatableBlock>(blocks: T[]): T[] {
  return blocks.filter((b) => b.type === 'text' || blockHasContent(b));
}

/**
 * [공통] 00 §4.2 — 이모지 전 클래스 불허. Covers every user-authored string a block can carry:
 * the WYSIWYG body / 설명 (both live in `text`) and the image/video 제목. A video URL is a URL,
 * so it is left to the host allow-list.
 */
export function blockHasEmoji(b: ValidatableBlock): boolean {
  return hasEmojiInRichText(b.text) || hasEmoji(b.title);
}

// Re-exported so this module is the single import site for block validation; the host
// allow-list itself is owned by `video-embed` (shared with the preview renderers).
export { isAllowedVideoUrl };

/** The shared rules, as codes. Each feature maps a code to its own i18n key. */
export type BlockRuleCode =
  | 'TOO_MANY_BLOCKS'
  | 'EMPTY_BLOCK'
  | 'TEXT_BLOCK_TOO_LONG'
  | 'VIDEO_URL_INVALID'
  | 'VIDEO_TITLE_INVALID'
  | 'IMAGE_TITLE_INVALID'
  | 'BLOCK_DESC_TOO_LONG'
  | 'EMOJI_NOT_ALLOWED';

export interface BlockRuleViolation {
  code: BlockRuleCode;
  /** Interpolation values for the message (e.g. `{ max: 500 }`); absent when the copy is static. */
  max?: number;
}

/**
 * Run the shared rule chain and return the FIRST violation, or null when every block passes.
 *
 * Both features render a single error string under the editor, so the order is the priority
 * order — structural limits first (count, emptiness), then per-field bounds, then the
 * character policy.
 *
 * The "≥N blocks" rule is NOT part of this chain: 건강백과 requires ≥1 block and reports it with
 * its own message, 상담매뉴얼 allows 0. A caller that enforces its own emptiness rule first
 * simply never reaches the EMPTY_BLOCK step here.
 */
export function firstBlockRuleViolation(blocks: ValidatableBlock[]): BlockRuleViolation | null {
  if (blocks.length > MAX_BLOCKS) {
    return { code: 'TOO_MANY_BLOCKS', max: MAX_BLOCKS };
  }
  if (blocks.some((b) => !blockHasContent(b))) {
    return { code: 'EMPTY_BLOCK' };
  }
  if (blocks.some((b) => b.type === 'text' && plainTextLength(b.text) > TEXT_BLOCK_MAX)) {
    return { code: 'TEXT_BLOCK_TOO_LONG', max: TEXT_BLOCK_MAX };
  }
  if (blocks.some((b) => b.type === 'video' && (b.url ?? '').trim() !== '' && !isAllowedVideoUrl(b.url))) {
    return { code: 'VIDEO_URL_INVALID' };
  }
  if (blocks.some((b) => b.type === 'video' && !isVideoTitleValid(b.title))) {
    return { code: 'VIDEO_TITLE_INVALID', max: VIDEO_TITLE_MAX };
  }
  if (blocks.some((b) => b.type === 'image' && !isImageTitleValid(b.title))) {
    return { code: 'IMAGE_TITLE_INVALID', max: IMAGE_TITLE_MAX };
  }
  if (
    // Only when the description is actually shown — the textarea is hidden while
    // "텍스트 설명 노출" is off, so validating it then would deadlock the save on a field
    // the user can neither see nor fix.
    blocks.some((b) => (b.type === 'image' || b.type === 'video') && b.showText && !isBlockDescValid(b.text))
  ) {
    return { code: 'BLOCK_DESC_TOO_LONG', max: BLOCK_DESC_MAX };
  }
  if (blocks.some(blockHasEmoji)) {
    return { code: 'EMOJI_NOT_ALLOWED' };
  }
  return null;
}
